import SignIn from '@/pages/SignIn.tsx';
import {type ReactNode, createContext, useCallback, useContext, useEffect, useState} from 'react';

type UserContextInterface = {
    user: Tokens | undefined;
    logOut: () => void;
};

type UserContextProviderProps = {children: ReactNode};

const userContextInitial: UserContextInterface = {
    user: undefined,
    // biome-ignore lint/suspicious/noEmptyBlockStatements: default fake logout
    logOut: () => {},
};

export type Tokens = {
    access_token: string;
    refresh_token: string;
    scope: string;
    id_token: string;
    expiry_date: number;
};

const UserContext = createContext<UserContextInterface>(userContextInitial);

const useUserContext = (): UserContextInterface => {
    const context = useContext(UserContext);

    if (Object.keys(context).length === 0) {
        throw new Error('useUserContext must be used within a UserContextProvider');
    }

    return context;
};

const url = new URL('/public/google/refresh', import.meta.env.VITE_APP_API_ENDPOINT);

const UserContextProvider = ({children}: UserContextProviderProps): React.ReactElement => {
    const [user, setUser] = useState<Tokens | null>(null);
    const [refreshTimer, setRefreshTimer] = useState<null | ReturnType<typeof setTimeout>>(null);

    useEffect(() => {
        const tokens = loadLocalStorage();
        setUser(tokens);
    }, []);

    const loginCallback = useCallback(
        (user: Tokens) => {
            setUser(user);

            if (refreshTimer) {
                clearTimeout(refreshTimer);
                setRefreshTimer(null);
            }
        },
        [refreshTimer],
    );

    useEffect(() => {
        if (!user) {
            return;
        }

        setLocalStorage(user);

        const expires = user.expiry_date - Date.now() - 120_000; //120 seconds before expires incase local clock is off
        if (expires < 0) {
            setUser(null);
            return;
        }

        const timeout = setTimeout(async () => {
            setRefreshTimer(null);
            const tokenResponse = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-type': 'application/json',
                },
                body: JSON.stringify({
                    refreshToken: user.refresh_token,
                }),
            });

            if (!tokenResponse.ok) {
                setUser(null);
                return;
            }

            const tokens = (await tokenResponse.json()) as Tokens;
            setUser(tokens);
        }, expires);

        setRefreshTimer(timeout);
        return () => {
            clearTimeout(timeout);
        };
    }, [user]);

    if (!user) {
        return <SignIn setUser={loginCallback} />;
    }

    const value: UserContextInterface = {
        user: user,
        logOut: () => {
            setUser(null);
            clearLocalStorage();
        },
    };
    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

const keyName = 'token';

const loadLocalStorage = (): Tokens | null => {
    const keyStore = localStorage.getItem(keyName);
    if (!keyStore) {
        return null;
    }

    const tokens = JSON.parse(keyStore) as Tokens;
    if (tokens.expiry_date && tokens.expiry_date > Date.now()) {
        return tokens;
    }
    return null;
};

const setLocalStorage = (tokens: Tokens) => {
    localStorage.setItem(keyName, JSON.stringify(tokens));
};

const clearLocalStorage = () => {
    localStorage.removeItem(keyName);
};

export {UserContextProvider, useUserContext};
